import React, { useState } from "react"
import { useSelector } from "react-redux"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterField, FilterSelect, FilterSegment } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "14px",
	boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
}

const mutedStyle = { color: "var(--dark-text-muted, #8791a1)", fontSize: "12px" }

const abcMeta = {
	A: { color: "#16a34a", bg: "var(--dark-tint-success, #e7f8ef)", label: "Первые 80 % расхода" },
	B: { color: "#e0762c", bg: "var(--dark-tint-warning, #fff4df)", label: "Следующие 15 %" },
	C: { color: "#dc2626", bg: "var(--dark-tint-danger, #fdecec)", label: "Остальные 5 %" },
}

const purchasedOptions = [
	{ id: 1, label: "Закупаемые" },
	{ id: 0, label: "С заготовками" },
]

const writeOffOptions = [
	{ id: 0, label: "Без списаний" },
	{ id: 1, label: "Со списаниями" },
]

function formatQty(value) {
	const number = Number(value || 0)
	if (Math.abs(number) >= 100) return formatMoney(number, 0)
	return number.toFixed(2).replace(/\.?0+$/, "").replace(".", ",")
}

function KpiCard({ title, value, sublabel, icon, color = "#2563eb" }) {
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body p-3">
				<div className="d-flex justify-content-between align-items-start">
					<div style={{ ...mutedStyle, textTransform: "uppercase", letterSpacing: "0.4px" }}>{title}</div>
					<div className="d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px", borderRadius: "11px", background: `${color}18`, color }}>
						<i className={`uil ${icon}`}></i>
					</div>
				</div>
				<h3 className="mb-0 mt-2" style={{ fontSize: "26px", fontWeight: 800, color: "var(--dark-text-strong, #111827)", letterSpacing: "-0.5px" }}>{value}</h3>
				{sublabel && <div style={{ ...mutedStyle, marginTop: "2px" }}>{sublabel}</div>}
			</div>
		</div>
	)
}

function AbcCards({ groups = [] }) {
	return (
		<div className="row g-3 mb-3">
			{groups.map(group => {
				const meta = abcMeta[group.category] || abcMeta.C
				return (
					<div className="col-md-4" key={group.category}>
						<div className="h-100" style={{ ...cardStyle, background: meta.bg, borderColor: `${meta.color}33`, padding: "16px" }}>
							<div className="d-flex justify-content-between align-items-start">
								<div>
									<div style={{ fontWeight: 800, color: meta.color }}>Категория {group.category}</div>
									<div style={mutedStyle}>{meta.label}</div>
								</div>
								<i className="uil uil-chart-pie" style={{ color: meta.color, fontSize: "20px" }}></i>
							</div>
							<div className="mt-3" style={{ fontSize: "28px", lineHeight: 1, fontWeight: 900, color: meta.color }}>{group.amount_short}</div>
							<div style={{ ...mutedStyle, marginTop: "6px" }}>{formatMoney(group.count, 0)} товаров · {formatMoney(group.share_percent, 1)}%</div>
						</div>
					</div>
				)
			})}
		</div>
	)
}

function ProductsTable({ items = [] }) {
	const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }
	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-center mb-2">
					<div>
						<h5 className="mb-0" style={{ fontWeight: 800 }}>ABC по товарам / ингредиентам</h5>
						<div style={mutedStyle}>Расход — фактические списания склада по себестоимости проводок; остаток — по средней себестоимости склада</div>
					</div>
					<span style={{ ...mutedStyle, background: "var(--dark-surface-2, #f1f4f8)", borderRadius: "14px", padding: "4px 10px", color: "var(--dark-text-muted, #5b6472)" }}>{formatMoney(items.length, 0)} товаров</span>
				</div>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>#</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Товар</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Расход</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Сумма расхода</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Доля</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Накоплено</th>
								<th className="py-3 text-muted fw-semibold text-center" style={headerThStyle}>ABC</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Остаток</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>В остатке</th>
							</tr>
						</thead>
						<tbody>
							{items.length === 0 && <tr><td colSpan="9" className="text-muted text-center py-4">Нет данных по выбранным фильтрам</td></tr>}
							{items.map(item => {
								const meta = abcMeta[item.abc_category] || abcMeta.C
								return (
									<tr key={item.product_id}>
										<td className="py-3 text-muted" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>{item.rank}</td>
										<td className="py-3" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>
											<div style={{ fontWeight: 800 }}>{item.product_name}</div>
											<div style={mutedStyle}>{item.unit_name || "—"} · себестоимость {formatMoney(item.cost_price)}</div>
										</td>
										<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>{formatQty(item.used_quantity)} {item.unit_name}</td>
										<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)", fontWeight: 800 }}>{item.used_amount_short}</td>
										<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>{formatMoney(item.share_percent, 1)}%</td>
										<td className="py-3 text-end text-muted" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>{formatMoney(item.cumulative_percent, 1)}%</td>
										<td className="py-3 text-center" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>
											<span style={{ background: meta.bg, color: meta.color, borderRadius: "12px", padding: "4px 10px", fontWeight: 900 }}>{item.abc_category}</span>
										</td>
										<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)", color: Number(item.balance) < 0 ? "#dc2626" : undefined }}>{formatQty(item.balance)} {item.unit_name}</td>
										<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)", fontWeight: 800 }}>{item.balance_value_short}</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

function InventoryAbc() {
	const reduxAccount = useSelector(state => state.account)
	const [data, setData] = useState(null)

	const filters = useAnalyticsFilters({
		...buildPeriod("30days"),
		pos_id: reduxAccount.pos_id ?? -1,
		wms_id: reduxAccount.wms_id ?? -1,
		category_id: -1,
		menu_id: -1,
		supplier_id: -1,
		only_purchased: 1,
		include_write_off: 0,
	}, searchWithFilter)
	const { draft, patch } = filters
	const options = useAnalyticsOptions(["warehouses", "suppliers", "menus", "categories"], draft.pos_id)

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl("/analytics/inventory-abc", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	const summary = data?.summary || {}
	const items = data?.items || []
	const groupA = (summary.abc || []).find(group => group.category === "A")

	return (
		<>
			<AnalyticsFilterBar
				title="ABC по товарам / ингредиентам"
				icon="uil-chart-pie-alt"
				subtitle="Какие товары дают основной расход склада"
				filters={filters}
				presets={["7days", "14days", "30days", "this_month", "last_month", "custom"]}
			>
				<FilterField label="Филиал">
					<FilterSelect options={options.poses} allLabel="Все филиалы" value={draft.pos_id} onChange={(id) => patch({ pos_id: id, wms_id: -1, supplier_id: -1 })} />
				</FilterField>
				<FilterField label="Склад">
					<FilterSelect options={options.warehouses} allLabel="Все склады" value={draft.wms_id} onChange={(id) => patch({ wms_id: id })} />
				</FilterField>
				<FilterField label="Категория">
					<FilterSelect options={options.categories} allLabel="Все категории" value={draft.category_id} onChange={(id) => patch({ category_id: id })} />
				</FilterField>
				<FilterField label="Меню">
					<FilterSelect options={options.menus} allLabel="Все меню" value={draft.menu_id} onChange={(id) => patch({ menu_id: id })} />
				</FilterField>
				<FilterField label="Поставщик">
					<FilterSelect options={options.suppliers} allLabel="Все поставщики" value={draft.supplier_id} onChange={(id) => patch({ supplier_id: id })} />
				</FilterField>
				<FilterField label="Позиции">
					<FilterSegment options={purchasedOptions} value={Number(draft.only_purchased)} onChange={(id) => patch({ only_purchased: id })} />
				</FilterField>
				<FilterField label="Списания в расходе">
					<FilterSegment options={writeOffOptions} value={Number(draft.include_write_off)} onChange={(id) => patch({ include_write_off: id })} />
				</FilterField>
			</AnalyticsFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl-4 col-md-6"><KpiCard title="Сумма расхода" value={summary.total_used_amount_short || "0"} sublabel={`${formatMoney(summary.items_total, 0)} товаров`} icon="uil-money-bill" color="#2563eb" /></div>
				<div className="col-xl-4 col-md-6"><KpiCard title="A-класс" value={groupA?.amount_short || "0"} sublabel={`${formatMoney(groupA?.count, 0)} товаров дают ${formatMoney(groupA?.share_percent, 1)}% расхода`} icon="uil-star" color="#e0762c" /></div>
				<div className="col-xl-4 col-md-6"><KpiCard title="Позиции" value={data?.only_purchased === false ? "С заготовками" : "Закупаемые"} sublabel={data?.only_purchased === false ? "заготовки и их сырьё считаются дважды" : "то, что покупается у поставщиков"} icon="uil-box" color="#16a34a" /></div>
			</div>

			<AbcCards groups={summary.abc || []} />
			<ProductsTable items={items} />
		</>
	)
}

export default InventoryAbc
